import type { Cycle, Phase } from "../hooks/useCycles";
import { resolveStepProgressStatus } from "./stepProgressStatus";

type FetPhase = Pick<Phase, "type" | "startDate" | "endDate">;

/**
 * The frozen-transfer steps before the transfer, as the treatment hub stores
 * them. Lining preparation is the `protocol` step on the `protocol` phase.
 */
const LINING_PREP = { stepId: "protocol", phaseType: "protocol" };
const TRANSFER = { stepId: "transfer", phaseType: "transfer" };
const STEP_IDS = ["pre_treatment", LINING_PREP.stepId, TRANSFER.stepId];

function step(
  which: { stepId: string; phaseType: string },
  phases: readonly FetPhase[],
  todayIso: string,
) {
  const phase = phases.find((candidate) => candidate.type === which.phaseType);
  return {
    phase,
    ...resolveStepProgressStatus({
      stepId: which.stepId,
      stepIds: STEP_IDS,
      phase,
      todayIso,
    }),
  };
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00`).getTime();
  const to = new Date(`${toIso}T00:00:00`).getTime();
  return Math.round((to - from) / 86_400_000);
}

export type FetTransferFacts = {
  /**
   * Where the transfer stands. `booked` is a date today or later; `overdue` is
   * a booked date already behind with the step not marked done, which the
   * post-procedure check-in asks about.
   */
  transfer:
    | { status: "booked"; on: string }
    | { status: "overdue"; on: string }
    | { status: "not-booked" };
  /**
   * The day of lining preparation, counting its start as day 1, once it has
   * started and while it is not done or skipped.
   */
  liningPrepDay: number | null;
  clinic: string | null;
};

/**
 * What the frozen-transfer Home can state for certain before the transfer,
 * read from the steps the treatment hub records and through the hub's own
 * status rule, so the two never disagree.
 */
export function fetTransferFacts(params: {
  cycle: Pick<Cycle, "provider"> | null | undefined;
  phases: readonly FetPhase[];
  todayIso: string;
}): FetTransferFacts {
  const { cycle, phases, todayIso } = params;

  const transfer = step(TRANSFER, phases, todayIso);
  const transferOn = transfer.phase?.startDate ?? null;
  const transferFacts: FetTransferFacts["transfer"] =
    transfer.status === "scheduled" && transferOn
      ? transferOn >= todayIso
        ? { status: "booked", on: transferOn }
        : { status: "overdue", on: transferOn }
      : { status: "not-booked" };

  const liningPrep = step(LINING_PREP, phases, todayIso);
  const prepStart = liningPrep.phase?.startDate ?? null;
  const liningPrepDay =
    liningPrep.status === "scheduled" && prepStart && prepStart <= todayIso
      ? daysBetween(prepStart, todayIso) + 1
      : null;

  return {
    transfer: transferFacts,
    liningPrepDay,
    clinic: cycle?.provider?.trim() || null,
  };
}
